import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
	AbstractControl,
	FormBuilder,
	FormGroup,
	ReactiveFormsModule,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { map, startWith } from 'rxjs';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { ApiDisabilityProofCreate } from '../../../../api/api-types/disability-proof.api';
import { Client } from '../../../../api/app-types/client.app';
import { DisabilityProof } from '../../../../api/app-types/disability-proof.app';
import { DisabilityStatus, DisabilityStatusInitial } from '../../../../api/app-types/disability-status.app';
import { DocumentFile, DocumentFileSubjectType } from '../../../../api/app-types/document.app';
import { DISABILITY_PROOF_SERVICE } from '../../../../api/services/disability-proof.service';
import { provideUploadApi } from '../../../../api/services/upload.api';
import { LvrRedirectNav } from '../../../../core/classes/redirect-nav.class';
import { LvrDefaultActions } from '../../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../../core/components/field/field';
import { LvrHeading } from '../../../../core/components/heading/heading';
import { LvrSearchableSelect } from '../../../../core/components/searchable-select/searchable-select';
import { LvrInputDirective } from '../../../../core/directives/lvr-input.directive';
import { DocumentUploadControl } from '../../../../shared/document-upload/document-upload';
import { allowedFileTypes, maxFiles, maxFileSize } from '../../../../shared/document-upload/document-upload-validators';
import { matDateProviders } from '../../../../shared/helpers/date.func';
import { hasError } from '../../../../shared/helpers/has-error.func';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { LvrAppendAsteriskPipe } from '../../../../shared/pipes/append-asterisk.pipe';
import { ClientResolved } from '../../../../shared/routing/resolvers/client.resolver';
import { LvrAutoSaveDirective } from '../../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../../shared/storage/form-persistence.service';
import { LvrRestoreActions } from '../../../../shared/storage/restore-actions';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { dateRangeValidator } from '../../../../shared/validators/date-range-validator';
import { ImmediateErrorStateMatcher } from '../../../../shared/validators/immediate-error-state-matcher';
import { maxDateValidator } from '../../../../shared/validators/max-date-validator';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../shared/notification/notification.factory';

@Component({
	selector: 'lvr-disability-proof-create',
	templateUrl: './disability-proof-create.html',
	styleUrl: './disability-proof-create.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		LvrPaddingDirective,
		ReactiveFormsModule,
		TranslocoPipe,
		MatFormFieldModule,
		MatDatepickerModule,
		MatInputModule,
		LvrInputDirective,
		LvrAppendAsteriskPipe,
		MatTooltip,
		LvrSearchableSelect,
		MatRadioModule,
		DocumentUploadControl,
		MatIconModule,
		LvrAutoSaveDirective,
		LvrRestoreActions,
		LvrField,
		LvrHeading,
		LvrDefaultActions,
	],
	providers: [matDateProviders, provideUploadApi],
})
export class DisabilityProofCreate extends LvrRedirectNav {
	public readonly hasError = hasError;
	public readonly subjectType = DocumentFileSubjectType.DISABILITY_REPORT;
	public readonly env = inject(ENVIRONMENT);
	public readonly PROOF_SETTINGS = this.env.proofSettings;
	public readonly maxRemarkLength = this.PROOF_SETTINGS.maxRemarkLength;
	public readonly maxOtherTextLength = this.env.maxTitleLength ?? 50;
	private readonly draftSrv = inject(FormPersistenceService);
	private readonly session = inject(SessionManagerService);
	private readonly api = inject(DISABILITY_PROOF_SERVICE);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly data = toSignal(inject(ActivatedRoute).data);
	private readonly clientResolved = computed<ClientResolved | undefined>(() => {
		return this.data()?.['client'] as ClientResolved | undefined;
	});
	public readonly client = computed<Client>(() => this.clientResolved()?.value as Client);
	private readonly statusData = computed(() => this.data()!['status'] as PaginatedResponse<DisabilityStatus>);
	private readonly proofs = computed(() => this.data()!['proofs'] as DisabilityProof[]);
	private readonly takenDates = computed(() => new Set(this.proofs().map((p) => p.since.toISOString())));
	public readonly disabilityStatus = computed(() => this.statusData().entities);
	public readonly dateFormatHint = this.env.visualDateFormat;
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly isLoading = signal<boolean>(false);

	private readonly sbStatusDurationRequired = new Set<DisabilityStatusInitial>([
		DisabilityStatusInitial.SEVERELY_DISABLED,
		DisabilityStatusInitial.EQUALIZED,
	]);

	public readonly form = this.initForm();
	private readonly _selectedStatus = toSignal(
		this.form.get('sbStatus')!.valueChanges.pipe(map(() => this.form.get('sbStatus')!.value)),
	);
	public readonly gdBValues = computed(() => {
		const selectedStatus = this._selectedStatus();
		if (!selectedStatus) return [];
		return this.disabilityStatus().find((status) => status.id === selectedStatus)?.relatedDisabilitySeverities ?? [];
	});
	public readonly draftId = FORM_KEYS.CREATE_DISABILITY_PROOF;
	public readonly canCreateDisabilityReportsPermission = hasPermission(
		this.session.permissions,
		'Documents.DisabilityReport.Create',
	);

	private readonly sbStatusCtrl = this.form.controls.sbStatus;
	private readonly otherTextCtrl = this.form.controls.otherText;
	private readonly sbStatusValue = toSignal(this.sbStatusCtrl.valueChanges, { initialValue: this.sbStatusCtrl.value });
	public readonly selectedSbStatus = computed(() => {
		const selectedId = this.sbStatusValue();

		if (selectedId == null) {
			return undefined;
		}

		return this.disabilityStatus().find((item) => item.id === selectedId);
	});
	public readonly shouldDisplayOtherText = computed(() => {
		return this.selectedSbStatus()?.initial === DisabilityStatusInitial.OTHER;
	});

	// Returns the maximum date for the SB-Ausweis
	// It can be max. today
	public get maxSbProofDate() {
		const today = new Date();
		return today;
	}

	// Returns the maximum date for the SB-Ausweis
	// It can be max. today + 50 years.
	public get maxLimitedDate() {
		const today = new Date();
		today.setFullYear(today.getFullYear() + this.PROOF_SETTINGS.maxExpiryYears);
		return today;
	}

	public get minLimitedDate() {
		const start = this.form.get('dateSbProof')?.value;
		return start ? new Date(start) : null;
	}

	public constructor() {
		super();
		this.handleOtherTextField();
	}

	private handleOtherTextField() {
		effect(() => {
			const needsOtherText = this.shouldDisplayOtherText();
			if (needsOtherText) {
				this.otherTextCtrl.addValidators([Validators.required, Validators.maxLength(this.maxOtherTextLength)]);
			} else {
				this.otherTextCtrl.clearValidators();
				if (this.otherTextCtrl.value) this.otherTextCtrl.setValue('', { emitEvent: false });
			}
			this.otherTextCtrl.updateValueAndValidity({ emitEvent: false });
		});
	}

	/**
	 * Checks if a form control has any of the given errors and is touched.
	 * @param control The form control to check.
	 * @param errors The names of the errors to look for.
	 * @returns True if any error exists and the control was touched.
	 */

	public async submitData() {
		if (this.isLoading()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const formData = this.form.value;

		const files = formData.files! as DocumentFile[];

		const data: ApiDisabilityProofCreate = {
			clientId: this.client().id,
			statusStart: formData.dateSbProof ? new Date(formData.dateSbProof).getISODateStringWithoutTime() : '',
			remark: formData.remark ?? '',
			statusEnd: formData.limitedUntil ? new Date(formData.limitedUntil).getISODateStringWithoutTime() : null,
			categoryDisabilityStatusId: Number.parseInt(formData.sbStatus!),
			documentIds: files.map((d: DocumentFile) => d.id),
			categoryDisabilitySeverityId: Number.parseInt(formData.gdbValue!),
			otherText: formData.otherText ? formData.otherText : undefined,
		};

		this.isLoading.set(true);

		try {
			await this.api.create(data);

			// Call the same as on cancel (save is done, so cleanup and redirect)
			await this.cancel();
			this.ntfy.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.disability_proof_created')),
			);
		} catch {
			this.ntfy.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.disability_proof_create_error')),
			);
		} finally {
			this.isLoading.set(false);
		}
	}

	public shouldDurationBeOptional() {
		const selectedStatus = this.selectedSbStatus();
		return !selectedStatus || !this.sbStatusDurationRequired.has(selectedStatus.initial as DisabilityStatusInitial);
	}

	public hasFileError(control: AbstractControl | null, ...errors: string[]) {
		if (!control) return '';

		const error = errors.find(
			(error) => control.hasError(error) && (control.dirty || !!control.value?.length || control.touched),
		);

		if (!error) return '';
		return error;
	}

	public async cancel() {
		this.draftSrv.deleteDraft('client', this.draftId);
		await this.navigateAfterFinished(['/desk/client', this.client().id, 'disabilities']);
	}

	private initForm() {
		const fb = inject(FormBuilder);
		const formGroup = fb.group({
			dateSbProof: [undefined, [Validators.required, maxDateValidator(this.maxSbProofDate), this.takenDateValidator()]],
			sbStatus: [undefined, [Validators.required]],
			limited: [''],
			limitedUntil: [undefined],
			gdbValue: [undefined, [Validators.required]],
			remark: ['', [Validators.maxLength(this.maxRemarkLength)]],
			otherText: [''],
			files: [
				[],
				[
					maxFiles(this.PROOF_SETTINGS.maxFiles),
					maxFileSize(this.env.fileSettings.maxFileSize),
					allowedFileTypes(this.env.fileSettings.allowedMimeTypes),
				],
			],
		});
		this.dateRangeValidator(formGroup);
		this.sbStatusDurationRequiredValidator(formGroup);

		return formGroup;
	}

	private dateRangeValidator(formGroup: FormGroup) {
		const sbDateCtrl = formGroup.get('dateSbProof');
		const limitedUntilCtrl = formGroup.get('limitedUntil');

		const applyValidator = () => {
			limitedUntilCtrl?.addValidators([
				dateRangeValidator(sbDateCtrl, limitedUntilCtrl),
				maxDateValidator(this.maxLimitedDate),
			]);
			limitedUntilCtrl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
		};

		limitedUntilCtrl?.valueChanges.pipe(startWith(limitedUntilCtrl.value), takeUntilDestroyed()).subscribe(() => {
			applyValidator();
		});

		sbDateCtrl?.valueChanges.pipe(startWith(sbDateCtrl.value), takeUntilDestroyed()).subscribe(() => {
			applyValidator();
		});

		applyValidator();
	}

	private sbStatusDurationRequiredValidator(formGroup: FormGroup) {
		const sbCtrl = formGroup.get('sbStatus');
		const limitedCtrl = formGroup.get('limited');
		const limitedUntilCtrl = formGroup.get('limitedUntil');
		const gdbCtrl = formGroup.get('gdbValue');

		const applyValidator = () => {
			const selectedStatusId = Number(sbCtrl?.value);
			const selectedStatus = this.disabilityStatus().find((status) => status.id === selectedStatusId);

			const requiresDuration =
				!!selectedStatus && this.sbStatusDurationRequired.has(selectedStatus.initial as DisabilityStatusInitial);

			if (requiresDuration) {
				limitedCtrl?.setValidators([Validators.required]);
			} else {
				limitedCtrl?.clearValidators();
				if (limitedCtrl?.value) {
					limitedCtrl.setValue('', { emitEvent: false });
				}
			}
			limitedCtrl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });

			const validatorsForLimitedUntil = [maxDateValidator(this.maxLimitedDate)];
			if (requiresDuration && limitedCtrl?.value === '2') {
				validatorsForLimitedUntil.unshift(Validators.required);
			}
			limitedUntilCtrl?.setValidators(validatorsForLimitedUntil);

			if (!(requiresDuration && limitedCtrl?.value === '2') && limitedUntilCtrl?.value != null) {
				limitedUntilCtrl.setValue(undefined, { emitEvent: false });
			}

			limitedUntilCtrl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
		};

		sbCtrl?.valueChanges.pipe(startWith(sbCtrl.value), takeUntilDestroyed()).subscribe(() => {
			gdbCtrl?.setValue(undefined, { emitEvent: false });
			applyValidator();
		});
		limitedCtrl?.valueChanges
			.pipe(startWith(limitedCtrl.value), takeUntilDestroyed())
			.subscribe(() => applyValidator());

		applyValidator();
	}

	/**
	 * Validator for knowing whether a Date has been already used for creating a Disability Proof.
	 * @returns `true` if a value is in the `takenDates` set.
	 */
	private takenDateValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (!value) return null;
			const isoDateStr = new Date(value).toISOString();
			return this.takenDates().has(isoDateStr) ? { dateTaken: true } : null;
		};
	}
}
